import { makeAutoObservable, runInAction } from "mobx";
import { useLocalObservable } from "mobx-react-lite";
import { createContext, useContext } from "react";
import { Example } from "./types";

export interface ExamplesSelectorConfig {
  selectedDataset?: string;
  selectedVersion?: number;
  selectedExamples?: Example[];
  visibleColumns?: string[];
  dataType?: "json" | "xml";
}

export class ExamplesStore {
  selectedDataset: string = "";
  selectedVersion: number = -1;
  visibleColumns: string[] = [];
  removedColumns: string[] = [];
  selectedExamples: Example[] = [];
  dataType: "json" | "xml" = "json";

  constructor(initialConfig?: ExamplesSelectorConfig) {
    if (initialConfig) {
      this.selectedDataset = initialConfig.selectedDataset || "";
      this.selectedVersion = initialConfig.selectedVersion || -1;
      this.selectedExamples = initialConfig.selectedExamples || [];
      this.visibleColumns = initialConfig.visibleColumns || [];
      this.dataType = initialConfig.dataType || "json";
    }
    makeAutoObservable(this);
  }

  setDataType(dataType: "json" | "xml") {
    this.dataType = dataType;
  }

  setRemovedColumns(removedColumns: string[]) {
    this.removedColumns = removedColumns;
  }

  setVisibleColumns(columns: string[]) {
    this.visibleColumns = columns;
  }

  toggleColumn(columnName: string) {
    runInAction(() => {
      const isAlreadyVisible = this.visibleColumns.includes(columnName);
      if (isAlreadyVisible) {
        this.visibleColumns = this.visibleColumns.filter(
          (c) => c !== columnName,
        );
        this.removedColumns = [...this.removedColumns, columnName];
      } else {
        this.removedColumns = this.removedColumns.filter(
          (c) => c !== columnName,
        );
        this.visibleColumns = [...this.visibleColumns, columnName];
      }
    });
  }

  setSelectedDataset(dataset: string) {
    runInAction(() => {
      this.selectedDataset = dataset;
      this.selectedVersion = -1;
      this.selectedExamples = [];
      this.visibleColumns = [];
    });
  }

  setSelectedVersion(version: number | string) {
    runInAction(() => {
      this.selectedVersion = Number(version);
      this.selectedExamples = [];
      this.visibleColumns = [];
    });
  }

  addExample(example: Example) {
    const isAlreadySelected = this.selectedExamples.some(
      (e) => e.id === example.id,
    );
    if (isAlreadySelected) {
      this.selectedExamples = this.selectedExamples.filter(
        (e) => e.id !== example.id,
      );
    } else {
      this.selectedExamples = [...this.selectedExamples, example];
    }
  }

  resetExamples() {
    this.selectedExamples = [];
  }

  removeExample(example: Example) {
    this.selectedExamples = this.selectedExamples.filter(
      (e) => e.id !== example.id,
    );
  }

  get config(): ExamplesSelectorConfig {
    return {
      selectedDataset: this.selectedDataset,
      selectedVersion: this.selectedVersion,
      selectedExamples: this.selectedExamples,
      visibleColumns: this.visibleColumns,
      dataType: this.dataType,
    };
  }

  get formattedExamples(): Record<string, any>[] {
    return this.selectedExamples.map((example) => {
      return this.visibleColumns.reduce(
        (acc, column) => {
          acc[column] = example.value[column];
          return acc;
        },
        {} as Record<string, any>,
      );
    });
  }
}

const ExamplesContext = createContext<ExamplesStore | null>(null);

export const ExamplesProvider = ({
  children,
  initialConfig,
}: {
  children: React.ReactNode;
  initialConfig?: ExamplesSelectorConfig;
}) => {
  const store = useLocalObservable(
    () => new ExamplesStore(initialConfig),
    // Only recreate if initialConfig reference changes
    [initialConfig],
  );
  return (
    <ExamplesContext.Provider value={store}>
      {children}
    </ExamplesContext.Provider>
  );
};

export const useExamples = () => {
  const context = useContext(ExamplesContext);
  if (!context) {
    throw new Error("useExamples must be used within an ExamplesProvider");
  }
  return context;
};

export default ExamplesProvider;
